<template>
  <div class="i-report">
    <iBackground color="white" />

    <div v-if="reportStore.isReportLoading">
      <iNavbarSimple />
      <iLoaderSimple :loading-animation-duration-ms="data.loadingAnimationDurationMs">
        <template #title>{{ reportStore.reportLoadingText.title }}</template>
        <template #subtitle>{{ reportStore.reportLoadingText.subtitle }}</template>
      </iLoaderSimple>
    </div>

    <div v-else>
      <template v-if="reportStore.invalidData">
        <iNavbarSimple />
        <iUnavailableCard
          text="Please try again later. If the issue persists please contact us at support@jobtest.org"
          title="Report unavailable"
        />
      </template>

      <template v-else>
        <template v-if="isFullScreenSection">
          <RouterView @open-modal-payment="openModalPayment()" />
        </template>

        <template v-else>
          <div class="dashboard">
            <div class="wrap">
              <div>
                <template v-if="hasNotifications">
                  <iGlobalNotification />
                </template>
                <template v-else-if="userStore.isCoachingHasNotEngaged">
                  <iCoachingNotice />
                </template>

                <iReportNavbar
                  :core-items="navigationItemsData.coreItems"
                  :profile-items="navigationItemsData.profileItems"
                  :support-items="navigationItemsData.supportItems"
                />

                <div class="content">
                  <div
                    class="grid"
                    :class="{ home: isReportHomePage, anchored: sectionHasAnchor }"
                  >
                    <div
                      class="anchors"
                      :style="{ '--display': anchoredDynamicUI.anchorDisplay }"
                    >
                      <template v-if="data.showAnchors">
                        <h3
                          v-if="Object.keys(data.anchors).length"
                          class="title"
                        >
                          Explore your result
                        </h3>
                        <div class="navigator">
                          <iPageAnchors
                            v-if="Object.keys(data.anchors).length"
                            :anchors="data.anchors"
                            :active-anchor="data.activeAnchor"
                            @set-active="setAnchorActive($event)"
                          />
                          <iCoachingAd v-if="userStore.isCoachingHasNotEngaged" />
                          <iPlanDetails v-if="userStore.isCoachingHasEngaged && isCoachingHomePage" />
                        </div>
                      </template>
                    </div>

                    <div
                      ref="routerViewRef"
                      class="router-view"
                      :style="{
                        '--bd-left': anchoredDynamicUI.routerLeftBorder,
                        '--padding': anchoredDynamicUI.routerPadding
                      }"
                    >
                      <RouterView
                        @open-modal-payment="openModalPayment()"
                        @load-anchors="loadPageAnchors($event.anchors, $event.autoScroll)"
                      />
                    </div>
                  </div>

                  <iReportNavbarMobile
                    v-if="!isLargeScreen && userStore.isReportUser"
                    :core-items="navigationItemsData.coreItems"
                    :profile-items="navigationItemsData.profileItems"
                    :support-items="navigationItemsData.supportItems"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>

        <iModalPayment
          v-bind="data.modalPaymentDetails"
          @click-on-close="closeModalPayment"
        >
          <template #center>
            <iPricingTable />
          </template>
        </iModalPayment>

        <iModalMessage
          v-bind="data.modalMessageDetails"
          @click-on-button="closeModalMessage"
          @click-on-close="closeModalMessage"
        />

        <iSubscriptionBundleModal
          v-if="subscriptionBundleModalVisibility"
          :visible="subscriptionBundleModalVisibility"
          :countdown="subscriptionBundleCountdown"
          @close="closeSubscriptionBundleModal"
          @go-to-bundle-page="closeModalAndGoToBundlePage"
        />

        <iBaseModal
          visible
          class="zigpoll-modal"
          :class="{ visible: data.showZigpollModal }"
          max-width="350px"
          :show-close-icon="false"
        >
          <div class="caption">Please complete this survey.</div>
          <div id="zigpoll-embed-target-report"></div>
          <div
            class="close"
            @click="data.showZigpollModal = false"
          >
            I decline to contribute.
          </div>
        </iBaseModal>

        <template v-if="data.lightBoxVideo">
          <iBaseLightBox @close="data.lightBoxVideo = ''">
            <video
              :src="data.lightBoxVideo"
              autoplay
              controls
              class="light-box-video"
              type="video/mp4"
            ></video>
          </iBaseLightBox>
        </template>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue"
import { RouterView, useRoute, useRouter } from "vue-router"

// STORES AND HELPERS
import { GlobalFunctions } from "@/helpers/globalFunctions"
import { GlobalVariables } from "@/helpers/globalVariables"
import { useReportStore } from "@/stores/report"
import { useUserStore } from "@/stores/user"

import navigationItems from "@/composables/navigation"
import useIsScreenSize from "@/composables/useIsScreenSize"
import hasVisitedOnboarding from "@/composables/useOnboarding"
import { useCountdown } from "@/composables/useCountdown"

// COMPONENTS
import iBaseLightBox from "@/components/base/BaseLightBox.vue"
import iBaseModal from "@/components/base/BaseModal.vue"
import iBackground from "@/components/global/Background.vue"
import iLoaderSimple from "@/components/global/LoaderSimple.vue"
import iNavbarSimple from "@/components/global/NavbarSimple.vue"
import iUnavailableCard from "@/components/global/UnavailableCard.vue"
import iModalMessage from "@/components/modals/ModalMessage.vue"
import iModalPayment from "@/components/modals/ModalPayment.vue"
import iPricingTable from "@/components/pricing/PricingTable.vue"
import iGlobalNotification from "@/components/report/GlobalNotification.vue"
import iSubscriptionBundleModal from "@/components/coaching/coachingBundle/SubscriptionBundleModal.vue"
import iCoachingAd from "@/components/report/CoachingAd.vue"
import iCoachingNotice from "@/components/report/navbar/CoachingNotice.vue"
import iReportNavbar from "@/components/report/navbar/ReportNavbar.vue"
import iReportNavbarMobile from "@/components/report/navbar/ReportNavbarMobile.vue"
import iPageAnchors from "@/components/report/PageAnchors.vue"
import iPlanDetails from "@/components/report/PlanDetails.vue"

// STYLES
import { zumthor100 } from "@/styles/colors"
import { breakpointLg, breakpointXl } from "@/styles/variables"

// TYPES
import type { PageAnchors } from "@/types/pageAnchors"

// STORES AND ROUTES
const activeRoute = useRoute()
const router = useRouter()
const reportStore = useReportStore()
const userStore = useUserStore()

// COMPOSABLES
const isLargeScreen = useIsScreenSize(breakpointLg)
const isExtraLargeScreen = useIsScreenSize(breakpointXl)
const subscriptionBundleCountdown = useCountdown({
  endAt: GlobalFunctions.setToLastDateTimeOfMonth(),
  fallbackDays: 15
})

// CONSTANTS
const DEFAULT_MODAL_MESSAGE_DETAILS = {
  buttonText: "",
  text: "",
  title: "",
  visible: false
}

const ZIGPOLL_DELAY_MS = 120000

// REFS
const routerViewRef = ref<HTMLElement | null>(null)
const observer = ref<MutationObserver>()
const zigpollRef = ref<HTMLElement | null>(null)
const subscriptionBundleModalVisibility = ref(false)

// DATA (reactive object with properties)

const data = reactive({
  loadingAnimationDurationMs: 4000,
  modalMessageDetails: { ...DEFAULT_MODAL_MESSAGE_DETAILS },
  modalPaymentDetails: { visible: false },
  showZigpollModal: false,
  showAnchors: false,
  anchors: {} as PageAnchors,
  activeAnchor: "",
  lightBoxVideo: ""
})

// COMPUTED

/**
 * Check if the current route is a full screen section, meaning that only the content of the route should be displayed, without the menu.
 */
const isFullScreenSection = computed(() => {
  return activeRoute.meta?.isFullScreen
})

const sectionHasAnchor = computed(() => {
  return activeRoute.meta?.isAnchored
})

const anchoredDynamicUI = computed(() => {
  const anchoredUI = {
    anchorDisplay: "block",
    routerLeftBorder: `1px solid ${zumthor100}`,
    routerPadding: isExtraLargeScreen.value ? "24px 0px" : "24px 0px 24px 24px"
  }
  const nonAnchoredUI = {
    anchorDisplay: "none",
    routerLeftBorder: "none",
    routerPadding: isReportHomePage.value ? "0px" : "24px 0px"
  }

  return sectionHasAnchor.value ? anchoredUI : nonAnchoredUI
})

const isReportHomePage = computed(() => activeRoute.name === GlobalVariables.urls.report.children.home.routeName)
const isCoachingHomePage = computed(() => activeRoute.name === GlobalVariables.urls.coaching.children.home.routeName)

const hasNotifications = computed(() => {
  return reportStore.notificationSystem.notifications.length > 0
})

const navigationItemsData = computed(() => navigationItems(openModalPayment))

// FUNCTIONS

function closeModalPayment() {
  data.modalPaymentDetails.visible = false
}

function openModalPayment() {
  data.modalPaymentDetails.visible = true
}

function closeModalMessage() {
  data.modalMessageDetails = { ...DEFAULT_MODAL_MESSAGE_DETAILS }
}

// FUNCTIONS (REPORT LOADING)
async function initPageSetup() {
  await userStore.loadUserData()

  if (!reportStore.isReportLoaded) await reportStore.loadReport()

  reportStore.notifyIfFewJobs()
  reportStore.notifyIfSkippedQuestions()
  reportStore.notifyIfNoJobs()
  reportStore.notifyIfReportOutdated()

  if (window.Zigpoll) reportStore.deactivateZigpollReload()
  const handleZigpollByDelay = () => {
    setTimeout(() => {
      if (
        [
          GlobalVariables.urls.report.children.feedback.routeName,
          GlobalVariables.urls.report.children.resumeBuilder.routeName,
          GlobalVariables.urls.report.children.coachingUpsell.routeName,
          GlobalVariables.urls.report.children.coachingUpsellB.routeName,
          GlobalVariables.urls.report.children.confirmation.routeName
        ].includes(activeRoute.name as string)
      )
        return handleZigpollByDelay()
      if (sessionStorage.getItem("free-upgrade-report-zigpoll-loaded") || reportStore.reportZigpollLoaded) return

      reportStore.loadZigpoll()
      reportStore.renderZigpoll(reportStore.getReportZigpollId)
      if (reportStore.isFreeUpgradeEligible) sessionStorage.setItem("free-upgrade-report-zigpoll-loaded", "true")
    }, ZIGPOLL_DELAY_MS)
  }
  handleZigpollByDelay()
}

const onEmbedZigpoll = (mutationsList: MutationRecord[]) => {
  for (const mutation of mutationsList) if (mutation.type === "childList") data.showZigpollModal = true
  if (observer.value) observer.value.disconnect()
  reportStore.deactivateZigpollReload()
}

const scrollToRouterView = () => {
  nextTick(() => {
    const onboardingTooltip = document.querySelector(".i-onboarding-tooltip")
    const offset = onboardingTooltip ? 206 : 72
    const element = onboardingTooltip ? onboardingTooltip : routerViewRef.value

    if (element) {
      // If OnboardingToltip is active, do not scroll to it when it is visible within the viewport
      if (onboardingTooltip) {
        const elementRect = element.getBoundingClientRect()
        const isVisible =
          elementRect.top > document.body.scrollTop &&
          elementRect.bottom < (window.innerHeight || document.documentElement.clientHeight)

        if (isVisible) return
      }

      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.scrollY - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: "instant"
      })
    }
  })
}

const loadPageAnchors = (anchors: PageAnchors, autoScroll = true) => {
  // Delete hidden anchors
  Object.keys(anchors).forEach((anchor) => {
    if (anchors[anchor].isHidden === true) delete anchors[anchor]
  })

  data.anchors = anchors
  data.showAnchors = true

  // Slight delay before scrolling so charts and graphs can complete loading
  if (autoScroll) {
    data.activeAnchor = activeRoute.hash.replace("#", "") || Object.keys(anchors)[0]
    setTimeout(() => {
      GlobalFunctions.scrollInToElement(`#${data.activeAnchor}`)
    }, 500)
  }

  // Listen to update active anchors on manual scroll
  window.addEventListener("scroll", updateActiveAnchorOnScroll)
}

const setAnchorActive = (anchor: string) => {
  GlobalFunctions.scrollInToElement(`#${anchor}`)
  data.activeAnchor = anchor
  history.replaceState(null, "", `#${anchor}`)
}

/**
 * Checks if a section meets every condition to set it's anchor as the active anchor
 * @param element - The element to check if it is actively visible
 * @param previousElement - The previous element to check if it's top is out of view
 * @returns - True if the element is scrolled into view, false otherwise
 */
const elementIsScrolledIntoView = (element: string, previousElement: string) => {
  const docViewTop = document.body.scrollTop
  const docViewMiddle = docViewTop + window.innerHeight / 1.5
  const docViewBottom = docViewTop + window.innerHeight

  const elemTop = (document.getElementById(element) as HTMLElement)?.getBoundingClientRect()?.top
  const elemBottom = elemTop + (document.getElementById(element) as HTMLElement)?.getBoundingClientRect()?.height

  const prevElemTop = (document.getElementById(previousElement) as HTMLElement)?.getBoundingClientRect()?.top

  // Return true if the top of the previous section is not still within the screen
  // AND
  // either it's top is above a third of the viewed screen or its bottom is within the viewed screen
  return prevElemTop < docViewTop + 85 && (elemTop <= docViewMiddle || elemBottom <= docViewBottom)
}

/**
 * Updates the active anchor as user scrolls on report page
 */
const updateActiveAnchorOnScroll = () => {
  const currentSection = Object.keys(data.anchors).reduce((a, b) => (elementIsScrolledIntoView(b, a) ? b : a))
  if (currentSection !== data.activeAnchor) {
    history.replaceState(null, "", `#${currentSection}`)
    data.activeAnchor = currentSection
  }
}

/**
 * Checks if the user is a paid coaching user, whether they have seen the subscription bundle modal, and if this page should show the modal.
 * If conditions match, it sets the modal to be visible.
 */
const setSubscriptionBundleModalVisibility = () => {
  setTimeout(() => {
    const coachingPaid = userStore.isCoachingPaiduser
    const subscriptionBundleModalSeen = localStorage.getItem("subscription-bundle-modal-seen")
    const showOnThisPage = [
      GlobalVariables.urls.report.children.home.routeName,
      GlobalVariables.urls.report.children.personality.routeName,
      GlobalVariables.urls.report.children.careers.routeName,
      GlobalVariables.urls.report.children.resources.routeName,
      GlobalVariables.urls.coaching.children.home.routeName,
      GlobalVariables.urls.coaching.children.selectPlan.routeName
    ].includes(activeRoute.name as string)
    const isExecutiveOnce = userStore.isCoachingOnce && userStore.hasPurchasedExecutiveDiscovery
    const isExecutiveSubscribedOrCanceled =
      userStore.isCoachingExecutiveOrProfessionalSubscriber || userStore.hasPurchasedExecutiveSubscription

    if (
      coachingPaid &&
      !subscriptionBundleModalSeen &&
      showOnThisPage &&
      !userStore.hasCoachingBundle &&
      !(isExecutiveOnce || isExecutiveSubscribedOrCanceled)
    ) {
      subscriptionBundleModalVisibility.value = true
    }
  }, 1500)
}

/**
 * Closes the group coaching modal and sets it as seen.
 */
const closeSubscriptionBundleModal = () => {
  subscriptionBundleModalVisibility.value = false
  localStorage.setItem("subscription-bundle-modal-seen", "true")
}

/**
 * Closes the group coaching modal, sets it as seen and redirects to bundle page.
 */
const closeModalAndGoToBundlePage = () => {
  closeSubscriptionBundleModal()
  router.push({
    name: GlobalVariables.urls.coaching.children.coachingBundle.routeName
  })
}

// LICYCLE HOOKS

onMounted(() => {
  initPageSetup()
  if (!hasVisitedOnboarding()) reportStore.setTooltipData(isExtraLargeScreen.value)

  observer.value = new MutationObserver(onEmbedZigpoll)
  const config = { childList: true, subtree: true, attributes: true }
  nextTick(() => {
    zigpollRef.value = document.querySelector("#zigpoll-embed-target-report")
    if (zigpollRef.value) observer.value?.observe(zigpollRef.value, config)
  })
  setSubscriptionBundleModalVisibility()
})

onBeforeUnmount(() => {
  window.removeEventListener("scroll", updateActiveAnchorOnScroll)
})

// WATCHERS

watch(activeRoute, () => {
  scrollToRouterView()
})

watch(
  () => activeRoute.name,
  () => {
    // Default each new route to not show anchors
    data.showAnchors = false

    window.removeEventListener("scroll", updateActiveAnchorOnScroll)
  }
)
</script>

<style scoped lang="scss">
.i-report {
  .dashboard {
    .wrap {
      width: 100%;

      .content {
        width: 100%;

        @include flex(column, start, center);

        .grid {
          width: 100%;
          padding: 24px 24px 96px; // large padding bottom to avoid footer overlap
          display: block;

          &.home {
            padding: 0;
            padding-bottom: 82px;

            @media screen and (min-width: $breakpoint-lg) {
              padding: unset;
              grid-template-columns: minmax(auto, 1440px);
            }

            @media screen and (min-width: $breakpoint-xxl) {
              grid-template-columns: minmax(auto, 1440px);
            }
          }

          @media screen and (min-width: $breakpoint-md) {
            padding: 24px 48px 96px; // large padding bottom to avoid footer overlap
          }

          @media screen and (min-width: $breakpoint-lg) {
            padding: 0 48px;
            display: grid;
            gap: 24px;
            width: fit-content;
            grid-template-columns: minmax(auto, 1012px);

            &.anchored {
              grid-template-columns: 268px minmax(auto, 720px);
            }
          }

          @media screen and (min-width: $breakpoint-xl) {
            gap: 48px;
            grid-template-columns: minmax(auto, 1274px);

            &.anchored {
              grid-template-columns: 268px minmax(auto, 958px);
            }
          }

          @media screen and (min-width: $breakpoint-xxl) {
            gap: 64px;
            grid-template-columns: minmax(auto, 1290px);

            &.anchored {
              grid-template-columns: 268px minmax(auto, 958px);
            }
          }

          .router-view {
            width: 100%;

            @media screen and (min-width: $breakpoint-lg) {
              padding: var(--padding);
              border-left: var(--bd-left);
            }
          }

          .anchors {
            position: relative;
            display: none;
            width: 100%;

            @media screen and (min-width: $breakpoint-lg) {
              display: var(--display);
              padding: 24px 0;
            }

            .title {
              display: none;

              @media screen and (min-width: $breakpoint-lg) {
                display: block;

                @include text-md;

                font-weight: 700;
                color: $pickle-bluewood900;
                margin: 0;
              }
            }

            .navigator {
              @include flex(column, start, stretch, $gap: 30px);

              width: 100%;
              padding: 18px 0;

              @media screen and (min-width: $breakpoint-lg) {
                height: auto;
                position: sticky;
                top: 80px;
                overflow-y: auto;
                -ms-overflow-style: none;
                scrollbar-width: none;

                &::-webkit-scrollbar {
                  display: none;
                }
              }
            }
          }
        }
      }
    }
  }

  .zigpoll-modal {
    visibility: hidden;

    &.visible {
      visibility: visible;
    }

    .caption {
      @include text-md;

      font-weight: 600;
      margin-bottom: 15px;
    }

    .close {
      @include text-sm;

      color: $black300;
      margin-top: 15px;
      font-weight: 300;
      cursor: pointer;
    }
  }

  @media print {
    .i-report {
      background-color: none !important;
      print-color-adjust: exact;
      -webkit-print-color-adjust: exact;
      filter: opacity(1);

      .content {
        padding-bottom: 0 !important;
      }
    }

    .print-hide {
      display: none !important;
    }

    @page {
      size: 210mm 253mm;
      margin: 0;
    }
  }
}
</style>
